import { useTranslate } from "@lobby/ocb-intl";
import { clsx } from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, KeyboardEvent } from "react";
import { Chat, Player } from "../../entities";
import { Icon, useMobile, useToggle } from "../../shared";
import { APIError, Alert, useErrorTranslate } from "../../shared";
import {
  AddAttachmentBtn,
  AttachmentPreviewDialog,
  ConversationWindow,
  SendMessageBtn,
} from "./components";

interface SupportChatWindowProps {
  onMinimized: VoidFunction;
}

type MessageError = {
  code: number;
  message: string;
  timestamp: number;
  playerId?: number;
  hallId?: number;
};

type PendingMessage = {
  text: string;
  timestamp: number;
};

const MAX_MESSAGE_LENGTH = 4096;

export function SupportChatWindow({ onMinimized }: SupportChatWindowProps) {
  const [isExpanded, toggleWindowSize] = useToggle(false);
  const [isMinimized, _, minimizeWindow] = useToggle(false);
  const [inputMessage, setInputMessage] = useState("");
  // const [isMessagesLoaded, setIsMessagesLoaded] = useState(false);
  const [error, setError] = useState<MessageError | null>(null);
  const [pendingMessages, setPendingMessages] = useState<PendingMessage[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isAttachmentDialogOpen, setIsAttachmentDialogOpen] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const isMobile = useMobile();
  const { $t } = useTranslate();
  const { formatMessage: translateError } = useErrorTranslate();

  const player = Player.usePlayer();
  const conversations = Chat.useConversationList();
  const { mutate } = Chat.useSendMessage();
  const uploadAttachment = Chat.useUploadAttachment();

  const conversation = conversations.data?.result?.[0] ?? null;
  const conversationId = conversation?.id ?? null;

  const isAttachmentButtonActive = conversationId !== null;

  const isOverflowLimitMessageLength = useMemo(
    () => inputMessage.length >= MAX_MESSAGE_LENGTH,
    [inputMessage]
  );

  function validateFile(file: File): Promise<string | null> | string | null {
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");

    // Check file type
    if (!isImage && !isVideo) {
      return $t({
        defaultMessage:
          "Only image (jpg, png) and video (mp4, mov, avi) files are allowed",
      });
    }

    // Check file size
    const maxSize = isImage ? 10 * 1024 * 1024 : 50 * 1024 * 1024; // 10MB for images, 50MB for videos
    if (file.size > maxSize) {
      if (isImage) {
        return $t({ defaultMessage: "Image size must be less than 10MB" });
      } else {
        return $t({ defaultMessage: "Video size must be less than 50MB" });
      }
    }

    // Check image dimensions
    if (isImage) {
      return new Promise<string | null>((resolve) => {
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(img.src);
          // if (img.width > 1920 || img.height > 1920) {
          //   resolve(
          //     $t({
          //       defaultMessage: "Image dimensions must be less than 1920x1920",
          //     })
          //   );
          // } else {
          //   resolve(null);
          // }
          resolve(null);
        };
        img.onerror = () => {
          URL.revokeObjectURL(img.src);
          resolve($t({ defaultMessage: "Failed to load image" }));
        };
        img.src = URL.createObjectURL(file);
      });
    }

    return null;
  }

  async function handleFileSelect(file: File) {
    const error = await validateFile(file);
    if (error) {
      setErrorWrapper({ code: 0, message: error });
      return;
    }

    setSelectedFile(file);
    setIsAttachmentDialogOpen(true);
    setAttachmentError(null);
  }

  function handleAttachmentCancel() {
    setSelectedFile(null);
    setIsAttachmentDialogOpen(false);
    setAttachmentError(null);
  }

  async function handleAttachmentSend() {
    if (!selectedFile || conversationId === null) return;

    setAttachmentError(null);

    uploadAttachment.mutate(
      {
        conversationId,
        file: selectedFile,
      },
      {
        onSuccess: async () => {
          handleAttachmentCancel();
        },
        onError: (error) => {
          setAttachmentError(
            error instanceof Error
              ? error.message
              : $t({ defaultMessage: "Failed to upload file" })
          );
        },
      }
    );
  }

  function clearInputMessage() {
    setInputMessage("");
  }

  function prepareMessage(message: string) {
    const computedMessage = message.trim();

    if (isOverflowLimitMessageLength) {
      throw new Error("Message is too long");
    }

    return computedMessage;
  }

  function sendMessage(message: string) {
    setPendingMessages((prev) => [
      ...prev,
      {
        text: message,
        timestamp: Date.now(),
      },
    ]);

    mutate(
      {
        ...(conversationId !== null && { conversationId }),
        text: message,
      },
      {
        onSettled: (data) => {
          setPendingMessages([]);

          if (data?.error) {
            setErrorWrapper({
              code: data.error.code,
              message: data.error.message,
            });
          }

          if (conversationId === null) {
            conversations.refetch();
          }
        },
      }
    );
  }

  function setErrorWrapper(error: { code: number; message: string }) {
    setError({
      code: error.code,
      message: translateError(new APIError(error.message, { code: error.code })),
      playerId: player.data?.id,
      hallId: player.data?.hallId,
      timestamp: Date.now(),
    });
  }

  function processInputMessage() {
    const message = prepareMessage(inputMessage);
    if (message) {
      sendMessage(message);
      clearInputMessage();
    }
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    setInputMessage(e.target.value);
  }

  function handleSendMessageBtnClick(e: React.MouseEvent<HTMLButtonElement>) {
    processInputMessage();
  }

  function handleInputKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      processInputMessage();
    }
  }

  useEffect(() => {
    if (conversations.data?.error) {
      setErrorWrapper(conversations.data.error);
    }
  }, [conversations.data?.error]);

  // useEffect(() => {
  //   !isInputDisabled && inputRef.current?.focus();
  // }, [isInputDisabled]);

  // useEffect(() => {
  //   if (isMessagesLoaded && !isPending) {
  //     setIsInputDisabled(false);
  //   } else {
  //     setIsInputDisabled(true);
  //   }
  // }, [isMessagesLoaded, isPending]);

  return (
    <div
      className={clsx("support-chat-window", {
        "support-chat-window_expanded": isExpanded,
        "support-chat-window_minimized": isMinimized,
        "modal-dialog": isMobile && !isMinimized,
      })}
      onAnimationEnd={({ animationName }) => {
        animationName === "minimize-chat-window" && onMinimized();
      }}
    >
      {isMinimized ? (
        <div className="absolute inset-0 rounded-inherit" />
      ) : (
        <div className="flex h-full flex-col gap-1">
          <div className="-mt-1 -mx-1 flex items-end justify-between mobile-only:p-2 mobile-only:pb-0 text-18">
            <button
              type="button"
              onClick={() => minimizeWindow(true)}
              className="support-chat-window__control-btn"
            >
              <Icon name="minimizeWindow" />
            </button>
            <button
              type="button"
              onClick={toggleWindowSize}
              className="support-chat-window__control-btn mobile-only:hidden"
            >
              <Icon name="expandWindow" />
            </button>
          </div>
          <div className="relative flex min-h-0 grow flex-col">
            <ConversationWindow
              conversationId={conversationId}
              pendingMessages={pendingMessages}
              isChatWindowExpanded={isExpanded} // Required to scroll to bottom when chat window is expanded
              onError={setErrorWrapper}
              // onMessagesLoading={setIsMessagesLoaded}
            />
            {isOverflowLimitMessageLength && (
              <span style={{ color: "var(--error-accent-color)" }}>
                {$t({ defaultMessage: "Message is too long" })}
              </span>
            )}
            <div className="support-chat-window__message-input-container">
              {isAttachmentButtonActive && (
                <AddAttachmentBtn onFileSelect={handleFileSelect} />
              )}
              <input
                ref={inputRef}
                name="message"
                type="text"
                value={inputMessage}
                placeholder={$t({
                  defaultMessage: "Type your message here...",
                })}
                autoComplete="off"
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
              />
              <SendMessageBtn
                disabled={isOverflowLimitMessageLength}
                onClick={handleSendMessageBtnClick}
              />
            </div>

            {error && (
              <div className="support-chat-window__alert-container">
                <Alert
                  message={error.message}
                  type="error"
                  closable
                  afterClose={() => setError(null)}
                  description={
                    <>
                      <div>Code: {error.code}</div>
                      <div>
                        PID: {error.playerId} / {error.hallId}
                      </div>
                      <div>{new Date(error.timestamp).toLocaleString()}</div>
                    </>
                  }
                />
              </div>
            )}

            <AttachmentPreviewDialog
              file={selectedFile}
              isOpen={isAttachmentDialogOpen}
              isUploading={uploadAttachment.isPending || pendingMessages.length > 0}
              error={attachmentError}
              onSend={handleAttachmentSend}
              onCancel={handleAttachmentCancel}
            />
          </div>
        </div>
      )}
    </div>
  );
}
